import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';

const API = process.env.REACT_APP_BACKEND_URL;

function OnboardingDashboard({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [processes, setProcesses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    template_id: '',
    start_date: new Date().toISOString().split('T')[0],
    assigned_buddy: '',
    hr_contact: user?.id || ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProcesses();
    fetchTemplates();
    fetchAnalytics();
    fetchEmployees();
  }, []);

  const fetchProcesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/onboarding-process`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProcesses(response.data.processes || []);
    } catch (error) {
      console.error('Error fetching processes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/onboarding-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/onboarding-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleStartOnboarding = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const template = templates.find(t => t.id === formData.template_id);
      const startDate = new Date(formData.start_date);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (template?.duration_days || 30));

      await axios.post(
        `${API}/api/onboarding-process`,
        {
          ...formData,
          expected_completion_date: endDate.toISOString().split('T')[0],
          tasks: []
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      showToast('Onboarding process started successfully!', 'success');
      setShowCreateForm(false);
      setFormData({
        employee_id: '',
        template_id: '',
        start_date: new Date().toISOString().split('T')[0],
        assigned_buddy: '',
        hr_contact: user?.id || ''
      });
      fetchProcesses();
      fetchAnalytics();
    } catch (error) {
      console.error('Onboarding start error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const calculateProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === 'Completed').length;
    return Math.round((completed / tasks.length) * 100);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Onboarding Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage employee onboarding processes</p>
          </div>
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1a1f3a] transition"
            >
              🚀 Start New Onboarding
            </button>
          )}
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{analytics.onboarding_processes.total}</p>
              <p className="text-sm text-blue-600 mt-1">Total Processes</p>
            </div>
            <div className="bg-yellow-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{analytics.onboarding_processes.in_progress}</p>
              <p className="text-sm text-yellow-600 mt-1">In Progress</p>
            </div>
            <div className="bg-green-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{analytics.onboarding_processes.completed}</p>
              <p className="text-sm text-green-600 mt-1">Completed</p>
            </div>
            <div className="bg-purple-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{analytics.onboarding_processes.average_completion_days}</p>
              <p className="text-sm text-purple-600 mt-1">Avg Days</p>
            </div>
            <div className="bg-orange-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-orange-700">{analytics.documents.pending}</p>
              <p className="text-sm text-orange-600 mt-1">Docs Pending</p>
            </div>
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Start New Onboarding</h2>
            <form onSubmit={handleStartOnboarding}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employee *</label>
                  <select
                    required
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} - {emp.designation}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template *</label>
                  <select
                    required
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  >
                    <option value="">Select Template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.duration_days} days)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Buddy (Optional)</label>
                  <select
                    value={formData.assigned_buddy}
                    onChange={(e) => setFormData({ ...formData, assigned_buddy: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  >
                    <option value="">None</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1a1f3a] transition disabled:opacity-50"
                >
                  {loading ? '⏳ Starting...' : '🚀 Start Onboarding'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition"
                >
                  ❌ Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Processes List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Active Onboarding Processes</h2>
          
          {processes.length === 0 ? (
            <p className="text-gray-500">No onboarding processes found.</p>
          ) : (
            <div className="space-y-4">
              {processes.map((process) => (
                <div key={process.id} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Employee: {process.employee_id}</h3>
                      <p className="text-sm text-gray-600">
                        Start: {new Date(process.start_date).toLocaleDateString()} → 
                        Expected End: {new Date(process.expected_completion_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(process.status)}`}>
                      {process.status}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span className="font-semibold">{calculateProgress(process.tasks)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="h-3 rounded-full bg-[#2b3380] transition-all"
                        style={{ width: `${calculateProgress(process.tasks)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <div>
                      <span>✅ {process.tasks.filter(t => t.status === 'Completed').length} / {process.tasks.length} tasks completed</span>
                      {process.assigned_buddy && <span className="ml-4">👤 Buddy: {process.assigned_buddy}</span>}
                    </div>
                    <a
                      href={`/onboarding-process/${process.id}`}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    >
                      📋 View Details
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default OnboardingDashboard;